import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Bell, 
  Check, 
  CheckCheck, 
  Mail, 
  MessageCircle, 
  AlertCircle,
  FileText,
  Rocket,
  Calendar,
  User,
  Trash2,
  ExternalLink,
  Filter,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import DashboardLayout from "../components/DashboardLayout";
import { EarnMoneyBanner } from "../components/Client/PlanSection";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, unread, read

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar notificaciones");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    } catch (error) {
      toast.error("Error al marcar como leída");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("Todas las notificaciones marcadas como leídas");
    } catch (error) {
      toast.error("Error al marcar todas como leídas");
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success("Notificación eliminada");
    } catch (error) {
      // If delete endpoint doesn't exist, just remove from UI
      setNotifications(prev => prev.filter(n => n.id !== id));
    }
  };

  const getNotificationStyle = (type, notification = {}) => {
    switch (type) {
      case 'new_chat_message':
      case 'chat':
        return { 
          icon: <MessageCircle className="w-5 h-5" />, 
          bg: 'bg-blue-100', 
          color: 'text-blue-600',
          label: 'Mensaje',
          action: notification.action_url || '/chat'
        };
      case 'step_advanced':
      case 'step_completed':
      case 'system':
        return { 
          icon: <Rocket className="w-5 h-5" />, 
          bg: 'bg-emerald-100', 
          color: 'text-emerald-600',
          label: 'Progreso',
          action: notification.action_url || '/dashboard'
        };
      case 'document_uploaded':
      case 'document_approved':
      case 'document_request':
      case 'documents_complete':
        return { 
          icon: <FileText className="w-5 h-5" />, 
          bg: 'bg-purple-100', 
          color: 'text-purple-600',
          label: 'Documento',
          action: notification.action_url || '/documents'
        };
      case 'interview_reminder':
      case 'appointment':
        return { 
          icon: <Calendar className="w-5 h-5" />, 
          bg: 'bg-amber-100', 
          color: 'text-amber-600',
          label: 'Cita',
          action: '/dashboard'
        };
      case 'alert':
      case 'urgent':
        return { 
          icon: <AlertCircle className="w-5 h-5" />, 
          bg: 'bg-red-100', 
          color: 'text-red-600',
          label: 'Alerta',
          action: '/dashboard'
        };
      case 'email':
        return { 
          icon: <Mail className="w-5 h-5" />, 
          bg: 'bg-indigo-100', 
          color: 'text-indigo-600',
          label: 'Email',
          action: null
        };
      default:
        return { 
          icon: <Bell className="w-5 h-5" />, 
          bg: 'bg-gold-100', 
          color: 'text-gold-600',
          label: 'Sistema',
          action: '/dashboard'
        };
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Navigate - prioritize action_url if available
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (notification.type === 'new_chat_message' || notification.type === 'chat') {
      navigate('/chat');
    } else {
      const style = getNotificationStyle(notification.type, notification);
      if (style.action) {
        navigate(style.action);
      }
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  
  const filteredNotifications = notifications.filter(n => {
    if (filter === "unread") return !n.read;
    if (filter === "read") return n.read;
    return true;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="notifications-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
              Notificaciones
            </h1>
            <p className="text-navy-600">
              {unreadCount > 0 ? `${unreadCount} sin leer` : "Estás al día"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchNotifications}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={markAllAsRead}
                className="text-navy-600"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Marcar todas
              </Button>
            )}
          </div>
        </motion.div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {[
            { key: "all", label: "Todas" },
            { key: "unread", label: `Sin leer (${unreadCount})` },
            { key: "read", label: "Leídas" }
          ].map(f => (
            <Button
              key={f.key}
              variant={filter === f.key ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f.key)}
              className={filter === f.key ? "bg-navy-900" : ""}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {filteredNotifications.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-elevated p-12 text-center"
          >
            <div className="w-16 h-16 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-navy-400" />
            </div>
            <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">
              {filter === "unread" ? "No hay notificaciones sin leer" : "No hay notificaciones"}
            </h3>
            <p className="text-navy-500">
              Te notificaremos cuando haya actualizaciones en tu caso
            </p>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notif, index) => {
              const style = getNotificationStyle(notif.type, notif);
              return (
                <motion.div
                  key={notif.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleNotificationClick(notif)}
                  className={`card-elevated p-4 flex items-start gap-4 cursor-pointer hover:shadow-md transition-all group ${
                    !notif.read ? "bg-gold-50 border-l-4 border-gold-500" : ""
                  }`}
                  data-testid={`notification-${notif.id}`}
                >
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${style.bg} ${style.color}`}>
                    {style.icon}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${style.bg} ${style.color}`}>
                        {style.label}
                      </span>
                      {!notif.read && (
                        <span className="w-2 h-2 bg-gold-500 rounded-full" />
                      )}
                    </div>
                    <p className={`${!notif.read ? "font-medium text-navy-900" : "text-navy-700"}`}>
                      {notif.message}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      <p className="text-sm text-navy-400">
                        {new Date(notif.sent_at || notif.created_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {style.action && (
                        <span className="text-sm text-emerald-600 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <ExternalLink className="w-3 h-3" />
                          Abrir
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {!notif.read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); markAsRead(notif.id); }}
                        className="text-navy-500 hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                      className="text-navy-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Earn Money Banner - Only for clients */}
        <div className="mt-8">
          <EarnMoneyBanner userRole={user?.role} />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Notifications;
